import { useId, useRef } from "react";
import type { ClipboardEvent, KeyboardEvent, ChangeEvent } from "react";
import { cn } from "./cn";

/**
 * The design's one-time-code input: `length` single-character boxes that behave
 * like one control.
 *
 * Controlled by a plain digit string, so the screen keeps holding the value in
 * its own form state and this stays free of any form library. The boxes are the
 * presentation; `value` is always the whole code.
 *
 * The three behaviours that make it feel like one field rather than six:
 * pasting a code fills every box from the caret onwards, backspace in an empty
 * box steps back and clears the one before it, and the arrows move between
 * boxes instead of within a one-character value.
 */
export function OtpInput({
  length = 6,
  value,
  onChange,
  label,
  hint,
  error,
  disabled,
  autoFocus,
  className,
}: {
  length?: number;
  value: string;
  onChange: (next: string) => void;
  label: string;
  hint?: string;
  error?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  className?: string;
}) {
  const id = useId();
  const describedBy = hint || error ? `${id}-desc` : undefined;
  const boxes = useRef<Array<HTMLInputElement | null>>([]);

  const digits = value.replace(/\D/g, "").slice(0, length);
  const focus = (index: number) => {
    const box = boxes.current[Math.min(Math.max(index, 0), length - 1)];
    box?.focus();
    box?.select();
  };

  /** Overwrite from `index` onwards — the shape both typing and pasting take. */
  const fillFrom = (index: number, incoming: string) => {
    const typed = incoming.replace(/\D/g, "");
    if (typed === "") {
      return;
    }
    const next = (
      digits.slice(0, index) +
      typed +
      digits.slice(index + typed.length)
    ).slice(0, length);
    onChange(next);
    focus(index + typed.length);
  };

  const change = (index: number) => (event: ChangeEvent<HTMLInputElement>) => {
    // A box holds one character, so anything longer is an autofilled code —
    // `autoComplete="one-time-code"` delivers the whole thing to one box.
    fillFrom(index, event.target.value);
  };

  const keyDown = (index: number) => (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Backspace") {
      event.preventDefault();
      if (digits[index]) {
        onChange(digits.slice(0, index) + digits.slice(index + 1));
        return;
      }
      // Empty box: the character the user meant to delete is the one behind.
      onChange(digits.slice(0, Math.max(index - 1, 0)) + digits.slice(index));
      focus(index - 1);
      return;
    }
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      focus(index - 1);
      return;
    }
    if (event.key === "ArrowRight") {
      event.preventDefault();
      focus(index + 1);
    }
  };

  const paste = (index: number) => (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    fillFrom(index, event.clipboardData.getData("text"));
  };

  return (
    <div className={cn("w-full", className)}>
      <span id={`${id}-label`} className="mb-1.5 block text-xs font-semibold text-ink-2">
        {label}
      </span>
      <div
        role="group"
        aria-labelledby={`${id}-label`}
        aria-describedby={describedBy}
        className="flex gap-2 sm:gap-2.5"
      >
        {Array.from({ length }, (_, index) => (
          <input
            key={index}
            ref={(node) => {
              boxes.current[index] = node;
            }}
            value={digits[index] ?? ""}
            onChange={change(index)}
            onKeyDown={keyDown(index)}
            onPaste={paste(index)}
            onFocus={(event) => event.currentTarget.select()}
            disabled={disabled}
            autoFocus={autoFocus && index === 0}
            inputMode="numeric"
            // Only the first box offers the platform's SMS autofill: six boxes
            // all claiming it makes the browser prompt six times.
            autoComplete={index === 0 ? "one-time-code" : "off"}
            aria-label={`Digit ${index + 1} of ${length}`}
            aria-invalid={error ? true : undefined}
            className={cn(
              "h-13 w-full min-w-0 rounded-field border bg-surface text-center font-display",
              "text-md font-bold text-ink tabular-nums outline-none transition-colors duration-150",
              "focus:border-brand focus:ring-4 focus:ring-brand/12 disabled:opacity-50",
              error ? "border-danger" : "border-line",
            )}
          />
        ))}
      </div>
      {hint || error ? (
        <p
          id={describedBy}
          className={cn("mt-1.5 text-2xs", error ? "text-danger-ink" : "text-ink-4")}
        >
          {error ?? hint}
        </p>
      ) : null}
    </div>
  );
}
